<template>
  <div class="first-screen">
    <div
      v-for="index in layerIndices"
      :key="index"
      class="background-layer"
      :class="{ active: isImageActive(index) }"
      :style="getImageStyles(index)"
    ></div>

    <div class="first-screen__content">
      <div class="search">
        <LegacyWrap>
          <MainPageSearch>
            <SliderNavigation
              v-if="isMobileOrTablet && currentSlide"
              :current-slide="currentSlide"
              @prev="goPrev"
              @next="goNext"
            />
          </MainPageSearch>
        </LegacyWrap>
      </div>
      <LegacyWrap class="wrapper">
        <VerticalsMenu class="verticals-menu" />

        <div class="main-page-info">
          <h1 class="title">{{ title }}</h1>
        </div>

        <CompanyAdvantages class="advantages-inside" :current-slide="currentSlide" @prev="goPrev" @next="goNext" />
      </LegacyWrap>
    </div>
    <div v-if="displayedImages.length" :key="progressKey" class="loading-process" :style="progressBarStyle" />
  </div>
  <CompanyAdvantages class="advantages-outside" :current-slide="currentSlide" @prev="goPrev" @next="goNext" />
</template>

<script lang="ts" setup>
import { dispatchMicrofrontendEvent } from '@experience/core-hostapp-share-lib';
import { useI18n } from '@experience/i18n';
import { computed, onBeforeMount, onMounted, watch } from 'vue';

import MainPageSearch from '@/features/MainPageSearch.vue';
import CompanyAdvantages from '@/features/mainScreen/CompanyAdvantages.vue';
import { useHeroCarousel } from '@/features/mainScreen/composables/useHeroCarousel';
import { useHeroImagePreloader } from '@/features/mainScreen/composables/useHeroImagePreloader';
import ru from '@/features/mainScreen/i18n/ru';
import SliderNavigation from '@/features/mainScreen/SliderNavigation.vue';
import VerticalsMenu from '@/features/VerticalsMenu.vue';
import type { MainHeroItem } from '@/shared/api/generated/api';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

interface SliderItem {
  name: string;
  link: string;
  img: string;
}

const props = defineProps<{
  heroItems?: MainHeroItem[];
  heroTitle?: string | null;
}>();

const { isMobile, isTablet } = useScreenSize();

const isMobileOrTablet = computed(() => isMobile.value || isTablet.value);

const sliderData = computed<SliderItem[]>(() =>
  (props.heroItems || []).map((item) => ({
    name: item.url_text,
    link: item.url,
    img: item.image_url,
  })),
);

const images = computed(() => sliderData.value.map((item) => item.img));

const { preloadedImages, preloadImages, syncImages } = useHeroImagePreloader(images);
const displayedImages = computed(() => (preloadedImages.value.length ? preloadedImages.value : images.value));
const imagesCounter = computed(() => displayedImages.value.length || 1);
const layerIndices = computed(() => Array.from({ length: imagesCounter.value }, (_, index) => index));
// Интервал автопрокрутки hero-карусели, мс (6 c на слайд).
const interval = 6000;
const progressBarStyle = { animationDuration: `${String(interval)}ms` };
const title = computed(() => props.heroTitle || t('defaultTitle'));

const { currentImageIndex, progressKey, goNext, goPrev, resume, reset } = useHeroCarousel(displayedImages, interval);

const isImageActive = (index: number) =>
  currentImageIndex.value === index && (!index || displayedImages.value.length > index);

const getImageStyles = (index: number) => {
  const imageUrl = displayedImages.value.length > index ? displayedImages.value[index] : undefined;
  return {
    backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
  };
};

const currentSlide = computed(() => {
  if (!sliderData.value.length) return undefined;

  return sliderData.value[currentImageIndex.value % sliderData.value.length];
});

onBeforeMount(async () => {
  try {
    await preloadImages();
  } catch {
    // Предзагрузка первого изображения — это оптимизация, а не обязательное
    // условие рендера: при ошибке (например, CORS на CDN) молча используем
    // необработанный URL через фолбэк в displayedImages.
  }
});

watch(
  () => props.heroItems,
  (newItems) => {
    if (newItems?.length) {
      // Список слайдов обновился извне (например, пришли данные BFF поверх
      // SSR-предзагрузки первого слайда) — синхронизируем без потери уже
      // предзагруженного первого изображения.
      syncImages();
      reset();
    }
  },
);

const verticalsMenuSelect = () => {
  /**
   * отправляем событие для подсвечивания пункта меню в "вертикалях"
   */
  dispatchMicrofrontendEvent('update-verticals-menu-active', {
    active: 'experiences',
  });
};

onMounted(() => {
  resume();

  verticalsMenuSelect();
});
</script>

<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */

.first-screen {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 720px;
  background-color: var(--alias-color-skeleton-default);

  .background-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: cover;
    opacity: 0;
    transition: opacity 1s ease-in-out;

    &.active {
      opacity: 1;
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    @include adaptivity.tablet {
      flex-direction: column-reverse;
    }

    @include adaptivity.mobile {
      .title {
        margin: 0;
        text-align: left;
      }
    }
  }

  &::after,
  &::before {
    position: absolute;
    z-index: 1;
    content: '';
    backdrop-filter: blur(2px);
  }

  &::after {
    top: 0;
    left: 0;
    width: 100%;
    height: 168px;
    background: linear-gradient(0deg, transparent 0%, rgb(0 0 0 / 40%) 100%);
    /* stylelint-disable color-named */
    mask: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
    /* stylelint-enable color-named */

    @include adaptivity.mobile {
      height: 110px;
    }
  }

  &::before {
    bottom: 0;
    left: 0;
    width: 100%;
    height: 190px;
    background: linear-gradient(180deg, rgb(6 4 4 / 0%) 0%, rgb(0 0 0 / 30%) 70%, rgb(0 0 0 / 40%) 96.63%);

    /* Маска для плавного размытия */
    /* stylelint-disable color-named */
    mask: linear-gradient(to top, black 0%, black 60%, transparent 100%);
    /* stylelint-enable color-named */

    @include adaptivity.mobile {
      height: 130px;
    }
  }

  @include adaptivity.tablet {
    height: 480px;
  }

  @include adaptivity.mobile {
    height: 424px;
  }
}

.first-screen__content .wrapper {
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: auto;

  .verticals-menu {
    display: none;

    @include adaptivity.tablet {
      display: flex;
    }
  }

  @include adaptivity.tablet {
    gap: 0;
    justify-content: space-between;
    height: 100%;
  }
}

.search {
  position: relative;
  top: 96px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @include adaptivity.tablet {
    top: unset;
    box-sizing: border-box;
    justify-content: flex-start;
    padding: var(--alias-space-32) 0 var(--alias-space-40) 0;
  }
}

.title {
  width: 100%;
  max-width: 700px;
  color: var(--alias-color-text-inverse);

  @include typography.base-1100-1050-extra;

  @include adaptivity.tablet {
    max-width: 500px;

    @include typography.base-900-850-extra;
  }

  @include adaptivity.mobile {
    max-width: 320px;

    @include typography.base-800-750-extra;
  }
}

.advantages-inside {
  height: 74px;

  @include adaptivity.tablet {
    display: none !important;
  }
}

.advantages-outside {
  display: none !important;
  margin: 24px 20px;

  @include adaptivity.tablet {
    display: flex !important;
  }
}

:deep(.search .site-search) {
  @include adaptivity.mobile {
    padding: 0;
  }
}

:deep(.search-dropdown) {
  width: 100% !important;
  max-height: 530px !important;

  @include adaptivity.mobile {
    top: 100% !important;
    width: 100vw !important;
  }
}

:deep(.search) {
  z-index: 3 !important;

  @include adaptivity.mobile {
    padding: 24px 0 0;
  }
}

.background-layer:nth-child(2) {
  background-position: center;
}

.loading-process {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1;
  height: 8px;
  background: var(--alias-color-border-inverted-subtler);
  backdrop-filter: blur(15px);
  animation: progress-fill linear forwards;

  @include adaptivity.tablet {
    height: 6px;
  }
}

@keyframes progress-fill {
  from {
    width: 0;
  }

  to {
    width: 100%;
  }
}
</style>
